import { RESULT, UID } from 'shared/api/query/constants';
import { accessPolicyChildren } from 'shared/api/query/edges';
import { accessPolicy as accessPolicyConfig, planning as planningConfig } from 'shared/api/query/configs.json';
import { computed, ref } from 'vue';
import {
  createEntityV2, deleteEntities, getEntities,
  updateEntityV2,
} from '@/store/actions';
import { children as planningChildren, planningDetails, planningList } from '@/api/query/planning';
import { useStore } from 'vuex';

export default function usePlanningsRepo() {
  const store = useStore();

  const plannings = computed(() => Object.values(store.state.planning));

  const createSingleLoading = ref(false);
  const createSingle = (entity) => {
    createSingleLoading.value = true;
    return createEntityV2(store, {
      entity,
      model: planningConfig.model,
      attributes: planningChildren,
      mutation: 'ENTITY_CREATED_TO_MAP',
    }).then((response) => {
      if (response.status !== 201) {
        throw new Error('failed to create planning');
      }
      return store.state.planning[response.data.uid];
    }).finally(() => {
      createSingleLoading.value = false;
    });
  };

  const updateSingleLoading = ref(false);
  const updateSingle = (entity) => {
    updateSingleLoading.value = true;
    store.commit('ENTITY_UPDATED_TO_MAP', { entity, model: planningConfig.model });
    return updateEntityV2(store, {
      entity,
      model: planningConfig.model,
      attributes: planningChildren,
      mutation: 'DO_NOTHING',
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error('failed to update planning');
      }
      return store.state.planning[response.data.uid];
    }).finally(() => {
      updateSingleLoading.value = false;
    });
  };

  const deleteSingleLoading = ref(false);
  const deleteSingle = (entity) => {
    deleteSingleLoading.value = true;
    return deleteEntities(store, {
      entities: [{ uid: entity.uid }],
      model: planningConfig.model,
      mutation: 'ENTITIES_DELETED_TO_MAP',
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error('failed to delete planning');
      }
    }).finally(() => {
      deleteSingleLoading.value = false;
    });
  };

  const deleteListLoading = ref(false);
  const deleteList = (entities) => {
    deleteListLoading.value = true;
    return deleteEntities(store, {
      entities: entities.map((entity) => ({ uid: entity.uid })),
      model: planningConfig.model,
      mutation: 'ENTITIES_DELETED_TO_MAP',
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error('failed to delete plannings');
      }
    }).finally(() => {
      deleteListLoading.value = false;
    });
  };

  const updateSingleAccessPolicy = ({ uid, accessPolicy }) => updateEntityV2(store, {
    entity: { uid, accessPolicy },
    model: planningConfig.model,
    attributes: [
      { attr: UID },
      {
        attr: planningConfig.edges.accessPolicy,
        model: accessPolicyConfig.model,
        children: accessPolicyChildren,
      },
    ],
    mutation: 'PLANNING_ACCESS_POLICY',
  }).then((response) => {
    if (response.status !== 200) {
      throw new Error('failed to update planning access policies');
    }
    return store.state.planning[response.data.uid];
  });

  const getListLoading = ref(false);
  const getList = ({ status, searchTerm }) => {
    getListLoading.value = true;
    return getEntities(store, {
      query: planningList({ status, searchTerm }),
      model: planningConfig.model,
      mutation: 'ENTITIES_RETRIEVED_TO_MAP',
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error('failed to fetch plannings');
      }
      const entities = response.data[RESULT];
      return entities.map((e) => store.state.planning[e.uid]);
    }).finally(() => {
      getListLoading.value = false;
    });
  };

  const getSingleLoading = ref(false);
  const getSingle = (id) => {
    getSingleLoading.value = true;
    return getEntities(store, {
      query: planningDetails(id),
      model: planningConfig.model,
      mutation: 'ENTITIES_RETRIEVED_TO_MAP',
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error('failed to fetch planning');
      }
      const entity = response.data[RESULT];
      return store.state.planning[entity.uid];
    }).finally(() => {
      getSingleLoading.value = false;
    });
  };

  return {
    plannings,

    createSingle,
    createSingleLoading,
    updateSingle,
    updateSingleLoading,
    deleteSingle,
    deleteSingleLoading,
    deleteList,
    deleteListLoading,
    updateSingleAccessPolicy,
    getList,
    getListLoading,
    getSingle,
    getSingleLoading,
  };
}
